/* global femcare */

/**
 * Handles the dynamic behavior of breadcrumbs collapsing into a dropdown list
 * when the width exceeds the container.
 */
femcare.breadcrumbs = {
    /**
     * Initializes breadcrumbs functionality.
     */
    init() {
        this.breadcrumbs = document.querySelector('.breadcrumbs');
        if (this.breadcrumbs) {
            this.breadcrumbsList = this.breadcrumbs.querySelector('.breadcrumbs__list');
            this.breadcrumbsItems = Array.from(this.breadcrumbs.querySelectorAll('.breadcrumbs__item'));
            this.breadcrumbsDotsButton = this.breadcrumbs.querySelector('.breadcrumbs__dots');
            this.dropdownContainer = this.breadcrumbs.querySelector('.breadcrumbs__dropdown-container');
            this.breadcrumbsDropdownAriaLabel = this.breadcrumbs.getAttribute('data-label-dropdown');

            this.breadcrumbsDropdown = null;
            this.dropdownEventListenersAttached = false;

            this.handleBreadcrumbCollapse();
            this.initWindowResizeHandler();
        }
    },

    /**
     * Handles the collapsing logic and manages creation/removal of the dropdown.
     */
    handleBreadcrumbCollapse() {
        const breadcrumbsContainerWidth = this.breadcrumbs.getBoundingClientRect().width;

        // Move items back first in case they were previously collapsed
        if (this.breadcrumbsDropdown) {
            while (this.breadcrumbsDropdown.firstChild) {
                this.breadcrumbsList.insertBefore(this.breadcrumbsDropdown.firstChild, this.dropdownContainer.nextSibling);
            }
        }

        let currentListWidth = this.breadcrumbsList.getBoundingClientRect().width;
        let itemsCounter = 1;

        // Determine if dropdown is needed
        const needsDropdown = currentListWidth > breadcrumbsContainerWidth;

        if (needsDropdown && !this.breadcrumbsDropdown && this.dropdownContainer) {
            this.createDropdown();
        }

        // If dropdown exists, collapse items into it
        if (this.breadcrumbsDropdown) {
            while (
                currentListWidth > breadcrumbsContainerWidth &&
                itemsCounter < this.breadcrumbsItems.length
            ) {
                const itemToMove = this.breadcrumbsItems[itemsCounter];

                if (!itemToMove.classList.contains('breadcrumbs__dropdown-container')) {
                    this.breadcrumbsDropdown.appendChild(itemToMove);
                }

                itemsCounter++;
                currentListWidth = this.breadcrumbsList.getBoundingClientRect().width;
            }

            // Show/hide dropdown container based on children
            if (this.breadcrumbsDropdown.children.length > 0) {
                this.dropdownContainer.classList.remove('invisible');
                this.dropdownContainer.removeAttribute('inert');
            } else {
                this.removeDropdown(); // cleanup if no items inside
            }
        }
    },

    /**
     * Creates dropdown <ol> and appends to container.
     */
    createDropdown() {
        this.breadcrumbsDropdown = document.createElement('ol');
        this.breadcrumbsDropdown.id = 'collapsedBreadcrumbs';
        this.breadcrumbsDropdown.classList.add('breadcrumbs__dropdown', 'invisible');
        this.breadcrumbsDropdown.setAttribute('aria-label', this.breadcrumbsDropdownAriaLabel);
        this.breadcrumbsDropdown.setAttribute('inert', '');
        this.dropdownContainer.appendChild(this.breadcrumbsDropdown);

        if (!this.dropdownEventListenersAttached) {
            this.initBreadcrumbDropdownToggle();
            this.dropdownEventListenersAttached = true;
        }
    },

    /**
     * Removes the dropdown if it exists.
     */
    removeDropdown() {
        if (this.breadcrumbsDropdown) {
            this.breadcrumbsDropdown.remove();
            this.breadcrumbsDropdown = null;
        }

        if (this.dropdownContainer) {
            this.dropdownContainer.classList.add('invisible');
            this.dropdownContainer.setAttribute('inert', '');
        }
    },

    /**
     * Toggles the dropdown visibility and handles ARIA + keyboard interactions.
     */
    initBreadcrumbDropdownToggle() {
        if (!this.breadcrumbsDotsButton) {
            return;
        }

        this.breadcrumbsDotsButton.addEventListener('click', (event) => {
            event.stopPropagation();

            const isExpanded = this.breadcrumbsDotsButton.getAttribute('aria-expanded') === 'true';
            this.breadcrumbsDotsButton.setAttribute('aria-expanded', !isExpanded);
            this.breadcrumbsDropdown.classList.toggle('invisible');
            this.breadcrumbsDotsButton.classList.toggle('active');

            if (!isExpanded) {
                this.breadcrumbsDropdown.removeAttribute('inert');
                this.breadcrumbsDropdown.firstChild?.querySelector('a')?.focus();
            } else {
                this.breadcrumbsDropdown.setAttribute('inert', '');
            }
        });

        document.addEventListener('click', (event) => {
            if (!event.target.closest('.breadcrumbs') && this.breadcrumbsDropdown && !this.breadcrumbsDropdown.classList.contains('invisible')) {
                this.breadcrumbsDropdown.classList.add('invisible');
                this.breadcrumbsDropdown.setAttribute('inert', '');
                this.breadcrumbsDotsButton.classList.remove('active');
                this.breadcrumbsDotsButton.setAttribute('aria-expanded', 'false');
            }
        });

        document.addEventListener('keydown', (event) => {
            if (
                event.key === 'Escape' &&
                this.breadcrumbsDropdown &&
                !this.breadcrumbsDropdown.classList.contains('invisible')
            ) {
                this.breadcrumbsDropdown.classList.add('invisible');
                this.breadcrumbsDropdown.setAttribute('inert', '');
                this.breadcrumbsDotsButton.setAttribute('aria-expanded', 'false');
                this.breadcrumbsDotsButton.focus();
            }
        });
    },

    /**
     * Handles debounce and resize recalculations.
     */
    initWindowResizeHandler() {
        const onResize = femcare.shared.util.debounce(() => {
            this.handleBreadcrumbCollapse();
        }, 300);

        window.addEventListener('resize', onResize);
    }
};
